import { type FC, type ReactNode, useCallback, useEffect, useState } from "react";

import { userIsInquirer, userIsLoggedIn } from "~/domains/user/utils/roles";

import PageTitle from "~/common/components/PageTitle/v1/PageTitle";
import useMediaQuery from "~/common/hooks/useMediaQuery";
import { Col, Row } from "~/common/components/Layout";
import { EmailsOrderListTable } from "./components/EmailsOrderListTable";

import { deleteAccount, getList, saveAccount } from "./api/broker";
import { storageEngine, TYPE } from "~/common/utils/storage";
import { type AccountInfoData } from "~/features/b2bAccountSelection/components/AccountSelectionTabelView/api/GetAccountList";
import Spinner from "~/common/components/Spinner/Spinner";

import { type EmailOrderQuoteData } from "./types";
import { useEmailOrderQuotePopup } from "./components/EmailOrderQuotePopup";
import { userCanSeeMyCompanySettings } from "~/features/myAccount/dashboard/helper/permissions";
import { redirectToUrl } from "~/utils/url/urlUtils";
import { HOMEPAGE } from "~/utils/url/staticUrls";
import { useEmailOrderQuoteConfirmation } from "./components/EmailOrderQuotePopupConfirmation";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "~/common/utils/screenSizes";
import { useTranslation } from "react-i18next";
import { Button, styled } from "~/common/mui";
import { Alert } from "~/common/mui/Alert/Alert";
import AddIcon from "@mui/icons-material/Add";

interface OrderQuoteNotificationsPageViewProps {
  isDesktop: boolean;
  isInquirer: boolean;
  isLoading: boolean;
  hasError: boolean;
  emailAccounts: EmailOrderQuoteData[];
  userAccountsData: AccountInfoData[];
  onAddNew?: () => void;
  onRowUpdate?: (data: EmailOrderQuoteData) => void;
  onRowDelete?: (data: EmailOrderQuoteData) => void;
  onRowEdit?: (data: EmailOrderQuoteData) => void;
  modal?: ReactNode;
  modalConfirmation?: ReactNode;
  modalConfirmationVisible?: boolean;
}

export const OrderQuoteNotificationsPageView: FC<OrderQuoteNotificationsPageViewProps> = ({
  isDesktop,
  isInquirer,
  isLoading,
  hasError,
  emailAccounts,
  userAccountsData,
  onAddNew,
  onRowUpdate,
  onRowDelete,
  onRowEdit,
  modal,
  modalConfirmation,
  modalConfirmationVisible = false,
}) => {
  const { t } = useTranslation();

  const content = isLoading ? (
    <Spinner />
  ) : hasError ? (
    <Alert severity={"error"}>{t("page.orderQuoteSettings.status.error")}</Alert>
  ) : emailAccounts.length > 0 ? (
    <EmailsOrderListTable
      data={emailAccounts}
      availableAccounts={userAccountsData}
      onRowUpdate={onRowUpdate}
      onRowDelete={onRowDelete}
      onRowEdit={onRowEdit}
    />
  ) : (
    <Alert severity={"info"}>{t("page.orderQuoteSettings.status.noResults")}</Alert>
  );

  return (
    <OrderQuoteSettingsRoot className={isDesktop ? "desktop" : "mobile"}>
      <PageTitle title={t(`page.${isInquirer ? "quoteSettings" : "orderQuoteSettings"}.title`)} />
      <HeaderRow>
        <SubtitleCol size={9}>
          <h3 className="scha__order-quote-settings__section__title headline-small m-0">
            {t(`page.${isInquirer ? "quoteSettings" : "orderQuoteSettings"}.subtitle`)}
          </h3>
        </SubtitleCol>
        <AddNewButtonCol size={3}>
          <Button
            data-test-id={"scha__order-quote-settings-btn-add-new"}
            startIcon={<AddIcon sx={{ fontSize: { phone: 16, tablet: 20 } }} />}
            onClick={onAddNew}
          >
            {t("page.orderQuoteSettings.addNewEmail")}
          </Button>
        </AddNewButtonCol>
      </HeaderRow>
      <div className="scha__order-quote-settings__main">{content}</div>
      {!modalConfirmationVisible && modal}
      {modalConfirmation}
    </OrderQuoteSettingsRoot>
  );
};

export const OrderQuoteNotificationsPage: FC = () => {
  const canAccessPage = userCanSeeMyCompanySettings && userIsLoggedIn();
  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);

  // grab from session storage the accounts list
  let userAccountsString = canAccessPage ? storageEngine(TYPE.SESSION).getItem("userAccounts") || "" : "[]";
  if ("" === userAccountsString) {
    userAccountsString = "[]";
  }
  const userAccountsData: AccountInfoData[] = JSON.parse(userAccountsString) as AccountInfoData[];
  const isInquirer = canAccessPage && userIsInquirer();

  // internal states
  const [emailAccounts, setEmailAccounts] = useState<EmailOrderQuoteData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);

  const getEmailList = useCallback(() => {
    if (!canAccessPage) {
      return;
    }
    setIsLoading(true);
    getList()
      .then((data) => data.filter((account) => account.recipientEmail || account.recipientEmail.trim() !== "")) // filter out accounts that doesn't have proper emails set
      .then(setEmailAccounts)
      .then(() => setHasError(false))
      .catch(() => {
        setHasError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [canAccessPage]);

  // on init
  useEffect(getEmailList, [getEmailList]);

  const onRowUpdate = (data: EmailOrderQuoteData) => {
    // live update displayed account list without server side request
    const index = emailAccounts.findIndex((account) => account.recipientEmail === data.recipientEmail);
    if (index > -1) {
      const newList = [...emailAccounts];
      newList[index] = data;
      setEmailAccounts(newList);
    }
    saveAccount(data);
  };

  const onRowDelete = (data: EmailOrderQuoteData) => openConfirmation(data);

  const requestServerRowDelete = (data: EmailOrderQuoteData) => {
    // live update displaied account list without server side request
    const index = emailAccounts.findIndex((account) => account.recipientEmail === data.recipientEmail);
    if (index > -1) {
      const newList = [...emailAccounts];
      newList.splice(index, 1);
      setEmailAccounts(newList);
    }
    deleteAccount(data);
    // close modal if opened and user confirmed for deletion
    if (modal) {
      close();
    }
  };

  const onSubmit = (data: EmailOrderQuoteData) => saveAccount(data).finally(getEmailList);

  const { modal, open, close } = useEmailOrderQuotePopup({
    availableAccounts: userAccountsData,
    onSubmit,
    onDelete: onRowDelete,
  });

  const {
    modal: modalConfirmation,
    open: openConfirmation,
    visible: modalConfirmationVisible,
  } = useEmailOrderQuoteConfirmation({
    onOk: requestServerRowDelete,
    onCancel: (_) => {},
  });

  // guard component
  if (!canAccessPage) {
    redirectToUrl(HOMEPAGE);
    return null;
  }

  return (
    <OrderQuoteNotificationsPageView
      isDesktop={isDesktop}
      isInquirer={isInquirer}
      isLoading={isLoading}
      hasError={hasError}
      emailAccounts={emailAccounts}
      userAccountsData={userAccountsData}
      onAddNew={() => open()}
      onRowUpdate={onRowUpdate}
      onRowDelete={onRowDelete}
      onRowEdit={open}
      modal={modal}
      modalConfirmation={modalConfirmation}
      modalConfirmationVisible={modalConfirmationVisible}
    />
  );
};

const OrderQuoteSettingsRoot = styled("div")(({ theme }) => ({
  "& .scha__order-quote-settings__main__table .row": {
    margin: 0,
  },
  "& .col-header": {
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(1.25),
    borderBottom: `3px solid ${theme.palette.divider}`,
  },
  "& .col-body": {
    paddingTop: theme.spacing(2.5),
    paddingBottom: theme.spacing(2.5),
    cursor: "default",
    "& label": {
      cursor: "pointer",
    },
  },
  "& .scha__lc__card": {
    marginTop: theme.spacing(2.5),
    marginBottom: theme.spacing(2.5),
  },
}));

const HeaderRow = styled(Row)(({ theme }) => ({
  marginBottom: theme.spacing(3.75),
}));

const SubtitleCol = styled(Col)({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
});

const AddNewButtonCol = styled(Col)({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
});
